import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { FallbackConfig } from '../types';
import { getAnalyticsTracker } from '../services/analytics';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled(motion.div)<{ theme: any }>`
  background: ${props => props.theme.colors.modalBg};
  border: 2px solid ${props => props.theme.colors.modalBorder};
  border-radius: 20px;
  padding: 30px;
  max-width: 350px;
  width: 90%;
  text-align: center;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const FallbackImage = styled.img`
  width: 150px;
  height: 150px;
  object-fit: cover;
  border-radius: 25px;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(0, 0, 0, 0.1);
  border: none;
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.2);
    transform: scale(1.1);
  }
`;

const Title = styled.h2<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0 0 20px 0;
  line-height: 1.4;
`;

const FallbackButton = styled(motion.button)<{ theme: any }>`
  padding: 16px 32px;
  background: ${props => props.theme.colors.button};
  color: ${props => props.theme.colors.text};
  border: none;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover {
    background: ${props => props.theme.colors.buttonHover};
    transform: translateY(-2px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);

    &::before {
      left: 100%;
    }
  }
`;

interface FallbackModalProps {
  fallback: FallbackConfig;
  theme: any;
  onClose: () => void;
  pageId?: string;
}

const FallbackModal: React.FC<FallbackModalProps> = ({ fallback, theme, onClose, pageId }) => {
  const handleFallbackClick = async () => {
    // Track fallback engagement/conversion with proper logic
    if (pageId) {
      try {
        const analytics = getAnalyticsTracker(pageId);

        // Only count as conversion if they NEVER clicked main button
        // Track fallback button click
        await analytics.trackButtonClick('fallback_button', fallback.buttonText, fallback.buttonLink);
      } catch (error) {
        console.error('Failed to track fallback conversion:', error);
      }
    }

    if (fallback.buttonLink) {
      // Validate URL - ensure it starts with http:// or https://
      let validUrl = fallback.buttonLink;
      if (!fallback.buttonLink.startsWith('http://') && !fallback.buttonLink.startsWith('https://')) {
        validUrl = 'https://' + fallback.buttonLink;
      }
      window.location.href = validUrl;
    }
  };

  return (
    <AnimatePresence>
      <ModalOverlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <ModalContent
          theme={theme}
          initial={{ scale: 0.8, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>

          <Title theme={theme}>
            {fallback.title}
          </Title>

          {fallback.imageUrl && (
            <FallbackImage src={fallback.imageUrl} alt="Fallback" />
          )}

          <FallbackButton
            theme={theme}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleFallbackClick}
          >
            {fallback.buttonText}
          </FallbackButton>
        </ModalContent>
      </ModalOverlay>
    </AnimatePresence>
  );
};

export default FallbackModal;
